import { initializeApp } from 'firebase/app';
import { getAnalytics } from 'firebase/analytics';
import {
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink
} from 'firebase/auth';
import { VMAuthStorage } from './voteMachine/vmAuthStorage';
import { lambdaURL } from "./config";
import {  sendRequest } from "./voteMachine/vmUtils";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);

const actionCodeSettings = {
  url: "https://votemachine.org",
  handleCodeInApp: true
};

const firebaseService = {
  async signIn(email, password) {
    try {
      if (!auth) {
        console.error('Firebase Auth not initialized');
        return {
          success: false,
          error: 'Firebase Auth not initialized'
        };
      }

      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const idToken = await userCredential.user.getIdToken();
      VMAuthStorage.setAuthData(idToken, userCredential.user.uid);

      const getUserResponse = await sendRequest(lambdaURL, {
        command: "getUser",
        firebaseUid: userCredential.user.uid
      });

      if (!getUserResponse.body) {
        throw new Error('Failed to fetch user from system');
      }

      const userData = JSON.parse(getUserResponse.body);
      //localStorage.setItem('systemUserId', userData.uuid);
      VMAuthStorage.setSystemUserId(userData.uuid)

      return {
        success: true,
        userId: userData.uuid,
        token: idToken,
        isAdmin: userData.isAdmin
      };
    } catch (error) {
      VMAuthStorage.clearAllAuthData();
      console.error('Firebase sign in error:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // New method for sending magic link
  async sendSignInLink(email) {
    try {
      await sendSignInLinkToEmail(auth, email, actionCodeSettings);
      VMAuthStorage.setSignInEmail(email);
      return {
        success: true,
        message: 'Email link sent successfully'
      };
    } catch (error) {
      console.error('Error sending sign-in link:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  async completeSignInWithLink(email, href) {
    try {
      console.log("Completing sign-in with link:", { email, href });
      const auth = getAuth();

      if (!isSignInWithEmailLink(auth, href)) {
        console.error("Invalid sign-in link");
        throw new Error('Invalid or expired sign-in link');
      }

      // If there's a current user, sign them out first
      if (auth.currentUser) {
        await signOut(auth);
      }

      // Attempt the sign in
      const userCredential = await signInWithEmailLink(auth, email, href);
      const firebaseUser = userCredential.user;
      console.log("Firebase User after sign-in:", {
        uid: firebaseUser.uid,
        email: firebaseUser.email
      });

      const idToken = await userCredential.user.getIdToken();
      VMAuthStorage.setAuthData(idToken, userCredential.user.uid);

      // Check if user exists in our system
      const getUserResponse = await sendRequest(lambdaURL, {
        command: "getUser",
        email: email.toLowerCase(),
        firebaseUid: userCredential.user.uid
      });

      let userData;
      if (!getUserResponse?.body || getUserResponse.body === "null") {
        // User doesn't exist in our system, create them
        const createUserResponse = await sendRequest(lambdaURL, {
          command: "createUser",
          email: email.toLowerCase(),
          firebaseUid: userCredential.user.uid
        });

        if (!createUserResponse.body) {
          throw new Error('Failed to create user in system');
        }
        userData = JSON.parse(createUserResponse.body);
      } else {
        userData = JSON.parse(getUserResponse.body);
      }

      // Store the systemUserId
      VMAuthStorage.setSystemUserId(userData.uuid);

      return {
        success: true,
        userId: userData.uuid,
        token: idToken,
        isAdmin: userData.isAdmin
      };

    } catch (error) {
      console.error("Error completing sign-in with link:", {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      // Don't clear auth data here - let the calling code handle cleanup
      return {
        success: false,
        error: error.message
      };
    }
  },

  async register(email, password) {
    try {
      // First create the Firebase user
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const idToken = await userCredential.user.getIdToken();

      // Store the token BEFORE making the createUser request
      VMAuthStorage.setAuthData(idToken, userCredential.user.uid);

      const createUserResponse = await sendRequest(lambdaURL, {
        command: "createUser",
        email: email.toLowerCase(),
        firebaseUid: userCredential.user.uid
      });

      if (!createUserResponse.body) {
        throw new Error('Failed to create user in system');
      }

      const userData = JSON.parse(createUserResponse.body);
      VMAuthStorage.setSystemUserId(userData.uuid)
      return {
        success: true,
        userId: userData.uuid,
        token: idToken,
        isAdmin: userData.isAdmin
      };
    } catch (error) {
      console.error('Firebase registration error:', {
        code: error.code,
        message: error.message,
        fullError: error
      });
      return {
        success: false,
        error: error.message
      };
    }
  },

  async signOut() {
    try {
      await signOut(auth);
      VMAuthStorage.clearAllAuthData();
      return { success: true };
    } catch (error) {
      console.error('Firebase sign out error:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }
};

export { firebaseService };